'use client';

import { useState } from 'react';
import NextLink from 'next/link';
import { useTranslations, useLocale } from 'next-intl';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { LandingHeader, LandingFooter } from '../../../features/landing';

interface FormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
}

/**
 * ContactPageContent - Client component for the Contact Us page
 *
 * Simple contact form that sends an email to all super-admins.
 *
 * Story 6.7: Landing Page Header And Footer For Credibility
 */
export function ContactPageContent() {
  const t = useTranslations('creator.contact');
  const locale = useLocale();

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('errors.nameRequired');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('errors.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('errors.emailInvalid');
    }

    if (!formData.subject.trim()) {
      newErrors.subject = t('errors.subjectRequired');
    }

    if (!formData.message.trim()) {
      newErrors.message = t('errors.messageRequired');
    } else if (formData.message.trim().length < 10) {
      newErrors.message = t('errors.messageTooShort');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || t('errors.submitFailed'));
      }

      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage(error instanceof Error ? error.message : t('errors.submitFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof FormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  if (submitStatus === 'success') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <LandingHeader />

        <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 }, flex: 1 }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, md: 6 },
              textAlign: 'center',
              bgcolor: 'success.50',
              borderRadius: 2,
              border: 1,
              borderColor: 'success.200',
            }}
          >
            <CheckCircleIcon
              sx={{ fontSize: 64, color: 'success.main', mb: 2 }}
            />
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600, mb: 2 }}>
              {t('success.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              {t('success.message')}
            </Typography>
            <Button
              component={NextLink}
              href={`/${locale}`}
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              sx={{ textTransform: 'none' }}
            >
              {t('backToHome')}
            </Button>
          </Paper>
        </Container>

        <LandingFooter />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <LandingHeader />

      <Container maxWidth="sm" sx={{ py: { xs: 4, md: 6 }, flex: 1 }}>
        {/* Page Title */}
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 700,
            mb: 2,
            textAlign: 'center',
          }}
        >
          {t('title')}
        </Typography>

        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 4, textAlign: 'center' }}
        >
          {t('subtitle')}
        </Typography>

        {/* Contact Form */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            bgcolor: 'grey.50',
            borderRadius: 2,
          }}
        >
          {submitStatus === 'error' && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errorMessage}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              fullWidth
              label={t('form.name')}
              value={formData.name}
              onChange={handleChange('name')}
              error={!!errors.name}
              helperText={errors.name}
              disabled={isSubmitting}
              sx={{ mb: 3 }}
              inputProps={{ maxLength: 100 }}
            />

            <TextField
              fullWidth
              type="email"
              label={t('form.email')}
              value={formData.email}
              onChange={handleChange('email')}
              error={!!errors.email}
              helperText={errors.email}
              disabled={isSubmitting}
              sx={{ mb: 3 }}
              inputProps={{ maxLength: 255 }}
            />

            <TextField
              fullWidth
              label={t('form.subject')}
              value={formData.subject}
              onChange={handleChange('subject')}
              error={!!errors.subject}
              helperText={errors.subject}
              disabled={isSubmitting}
              sx={{ mb: 3 }}
              inputProps={{ maxLength: 200 }}
            />

            <TextField
              fullWidth
              multiline
              rows={5}
              label={t('form.message')}
              value={formData.message}
              onChange={handleChange('message')}
              error={!!errors.message}
              helperText={errors.message}
              disabled={isSubmitting}
              sx={{ mb: 3 }}
              inputProps={{ maxLength: 5000 }}
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={isSubmitting}
              endIcon={isSubmitting ? <CircularProgress size={20} /> : <SendIcon />}
              sx={{ textTransform: 'none', py: 1.5 }}
            >
              {isSubmitting ? t('form.sending') : t('form.submit')}
            </Button>
          </Box>
        </Paper>

        {/* Back to Home Link */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button
            component={NextLink}
            href={`/${locale}`}
            variant="text"
            startIcon={<ArrowBackIcon />}
            sx={{ textTransform: 'none' }}
          >
            {t('backToHome')}
          </Button>
        </Box>
      </Container>

      <LandingFooter />
    </Box>
  );
}

export default ContactPageContent;
